import React from "react";
import { IconInfo } from "@ui/icons";
import Tooltip from "@ui/tooltip";
import ComplexText from "@ui/complex-text";

const RawTooltip = ({ tooltip, iconCss }) => {
    if (!tooltip) return null;
    const css = `text-gray-400 text-sm ml-2 ${iconCss}`;
    return (
        <Tooltip content={tooltip}>
            <IconInfo className={css} />
        </Tooltip>
    );
};

const PostIconWrap = ({ icon }) => {
    if (!icon) return null;
    return (
        <div className="absolute text-gray-400 right-4 top-[17px]">{icon}</div>
    );
};

const HtmlLabel = (props) => {
    const { position, name, error, label, labelFloat, prefixSign } = props;

    const showLabel = label && !labelFloat;
    const showFloatingLabel = label && labelFloat;

    if (
        (position === "pre" && showLabel) ||
        (position === "post" && showFloatingLabel)
    ) {
        const labelCss = showFloatingLabel
            ? prefixSign
                ? `absolute left-14 top-3 text-xs text-inherit block peer-placeholder-shown:text-base peer-placeholder-shown:left-14 peer-placeholder-shown:top-5 peer-placeholder-shown:text-gray-400 peer-focus:text-inherit peer-focus:text-xs peer-focus:left-14 peer-focus:top-3 transition-all`
                : `absolute left-4 top-3 text-xs text-inherit block peer-placeholder-shown:text-base peer-placeholder-shown:left-4 peer-placeholder-shown:top-5 peer-placeholder-shown:text-gray-400 peer-focus:text-inherit peer-focus:text-xs peer-focus:left-4 peer-focus:top-3 transition-all`
            : error
            ? `mb-1 flex items-center text-skin-error`
            : `mb-1 flex items-center`;

        return (
            <label htmlFor={name} className={labelCss}>
                {label}

                {!showFloatingLabel && (
                    <RawTooltip tooltip={props.tooltip} iconCss="h-5 w-5" />
                )}
            </label>
        );
    } else {
        return null;
    }
};

const HtmlTooltip = (props) => {
    const { tooltip, label, labelFloat } = props;
    if (!tooltip) return null;
    const showLabel = label && !labelFloat;

    const css = showLabel
        ? `absolute right-2 top-[38px]`
        : `absolute right-2 top-[20px]`;
    return (
        <div className={css}>
            <RawTooltip tooltip={tooltip} />
        </div>
    );
};

const Prefix = (props) => {
    const { prefixSign, label, labelFloat } = props;

    if (!prefixSign) return null;

    const isLabelFloated = label && labelFloat;
    const css = isLabelFloated
        ? `absolute left-0 top-[0px] h-[65px] px-4 py-5 border-r border-gray-300 text-gray-400`
        : "absolute left-0 top-[29px] h-[46px] px-4 py-3 border-r border-gray-300 text-gray-400";

    return <div className={css}>{prefixSign}</div>;
};

const SelectPrimitiveOptions = React.memo((props) => (
    <React.Fragment>
        {props.options?.map((option, key) => (
            <option key={key} value={option.value}>
                {option.text}
            </option>
        ))}
    </React.Fragment>
));

const RawSelect = (props) => {
    const {
        name,
        className,
        attributes,
        onBlur,
        onChange,
        onKeyDown,
        readOnly,
        placeholder,
        value,
        options,
        autoFocus,
    } = props;

    return (
        <select
            id={name}
            name={name}
            className={className}
            {...attributes}
            onChange={onChange}
            autoFocus={autoFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            readOnly={readOnly}
            placeholder={placeholder}
            value={value}
        >
            <SelectPrimitiveOptions options={options} />
        </select>
    );
};

const RawInput = (props) => {
    const {
        name,
        className,
        attributes,
        onBlur,
        onChange,
        onKeyDown,
        readOnly,
        placeholder,
        type,
        value,
        min,
        autoFocus,
        maxLength,
    } = props;

    return (
        <input
            id={name}
            name={name}
            autoFocus={autoFocus}
            className={className}
            type={type ?? "text"}
            maxLength={maxLength}
            {...attributes}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            readOnly={readOnly}
            placeholder={placeholder}
            min={min}
            value={value}
        />
    );
};

const RawCheckbox = (props) => {
    const {
        name,
        className,
        attributes,
        onBlur,
        onChange,
        onKeyDown,
        readOnly,
        placeholder,
        value,
        checked,
        autoFocus,
    } = props;

    return (
        <input
            type="checkbox"
            id={name}
            name={name}
            autoFocus={autoFocus}
            className={className}
            {...attributes}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            readOnly={readOnly}
            placeholder={placeholder}
            defaultChecked={checked}
            value={value}
        />
    );
};

const CheckBoxGroup = (props) => {
    const { note, error, className, text, ...others } = props;
    const css = `text-skin-primary rounded border ${
        error
            ? "border-skin-error focus:ring-error"
            : "border-gray-300 focus:ring-primary"
    }`;
    return (
        <>
            <HtmlLabel {...others} position="pre" />
            <div className="flex items-center gap-2">
                <RawCheckbox className={css} {...props} />
                <ComplexText values={text} />
                <HtmlTooltip {...props} />
            </div>
            {note && <div className="text-sm">{note}</div>}
            {error && <div className="text-red-600 text-sm">{error}</div>}
        </>
    );
};

const HtmlInput = (props) => {
    const {
        className,
        label,
        labelFloat,
        error,
        note,
        tooltip,
        postIcon,
        prefixSign,
    } = props;

    const showFloatingLabel = label && labelFloat;

    const inputType =
        props.type === "Select" || props.type === "Checkbox"
            ? props.type
            : "Text";

    const baseCss = `w-full h-12 ${
        showFloatingLabel ? "pt-7 pb-3" : props.type === "Select" ? "py-0" : "py-4"
    } px-4 focus:ring-0 border w-full rounded-md`;
    const borderCss = error
        ? `border-red-600 focus:border-red-600`
        : "border-gray-300 focus:border-skin-primary";
    const textColorCss = error ? "text-red-600" : "";

    const peerCss = showFloatingLabel
        ? `peer placeholder-transparent`
        : `placeholder-gray-400`;

    const tooltipCss = tooltip ? "pr-10" : "";
    const postIconCss = postIcon ? "pr-10" : "";

    const prefixCss = prefixSign ? "pl-14" : "";
    const css = `${baseCss} ${textColorCss} ${borderCss} ${peerCss} ${tooltipCss} ${postIconCss} ${prefixCss} ${className}`;



    return (
        <div className="relative left-0 top-0 w-full">
            {inputType !== "Checkbox" && (
                <>
                    <HtmlLabel
                        {...props}
                        position="pre"
                        className={textColorCss}
                    />
                    <Prefix {...props} />
                    {inputType === "Select" && (
                        <RawSelect className={css} {...props} />
                    )}
                    {inputType === "Text" && (
                        <RawInput className={css} {...props} />
                    )}
                    {inputType === "Checkbox" && <RawCheckbox {...props} />}
                    <HtmlLabel
                        {...props}
                        position="post"
                        className={textColorCss}
                    />
                    {showFloatingLabel && <HtmlTooltip {...props} />}
                    {<PostIconWrap icon={postIcon} />}
                    {error && (
                        <div className="text-red-600 text-sm">{error}</div>
                    )}
                    {note && <div className="text-sm">{note}</div>}
                </>
            )}
            {inputType === "Checkbox" && <CheckBoxGroup {...props} />}
        </div>
    );
};

export default HtmlInput;
